import { useState, useEffect } from 'react';
import { Calendar, PhoneCall, Send, Eye, MousePointer, Repeat, TrendingUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/common/Dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { Pagination } from '@/components/common/Pagination';
import * as accountsService from '@/services/api/accountsService';
import type { AccountTimelineEvent, AccountTimelineResponse } from '@/services/api/accountsService';

interface MQLHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  mql: {
    id: string;
    accountId?: string;
    accountName: string;
    contactName: string;
    score: number;
    submittedDate: string;
  };
}

const eventIcons = {
  CALL: PhoneCall,
  MAGNET_DELIVERY: Send,
  ENGAGEMENT: Eye,
};

const eventSubTypeIcons = {
  OPEN: Eye,
  CLICK: MousePointer,
  REVISIT: Repeat,
};

export default function MQLHistoryDialog({ open, onClose, mql }: MQLHistoryDialogProps) {
  const [timeline, setTimeline] = useState<AccountTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (open && mql.accountId) {
      fetchTimeline();
    }
  }, [open, mql.accountId, currentPage]);

  const fetchTimeline = async () => {
    if (!mql.accountId) return;

    try {
      setLoading(true);
      const timelineData = await accountsService.getAccountTimeline(mql.accountId, {
        page: currentPage,
        limit: 10,
      });
      setTimeline(timelineData);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getEventIcon = (event: AccountTimelineEvent) => {
    if (event.type === 'ENGAGEMENT' && event.subType) {
      const IconComponent = eventSubTypeIcons[event.subType as keyof typeof eventSubTypeIcons];
      return IconComponent || Eye;
    }
    return eventIcons[event.type as keyof typeof eventIcons] || Calendar;
  };

  const getEventColor = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'text-green-600 bg-green-50',
      blue: 'text-blue-600 bg-blue-50',
      purple: 'text-purple-600 bg-purple-50',
      orange: 'text-orange-600 bg-orange-50',
    };
    return colorMap[color] || 'text-gray-600 bg-gray-50';
  };

  const isKeyQualificationEvent = (event: AccountTimelineEvent): boolean => {
    // Key events that likely contributed to MQL qualification
    if (event.type === 'CALL' && event.metadata?.connected) return true;
    if (event.type === 'ENGAGEMENT' && event.subType === 'CLICK') return true;
    if (event.type === 'MAGNET_DELIVERY' && event.metadata?.stageNumber >= 6) return true;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogClose onClose={onClose} />
        <DialogHeader>
          <DialogTitle className="text-2xl">Engagement History</DialogTitle>
          <DialogDescription>
            Complete interaction timeline for <span className="font-semibold">{mql.accountName}</span> leading to MQL submission
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* MQL Summary Card */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                MQL Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Account</div>
                  <div className="font-semibold">{mql.accountName}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Contact</div>
                  <div className="font-semibold">{mql.contactName}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">MQL Score</div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${mql.score}%` }}
                      />
                    </div>
                    <span className="font-bold text-primary">{mql.score}</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Submitted</div>
                  <div className="font-semibold">
                    {new Date(mql.submittedDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Engagement Statistics */}
          {timeline && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">{timeline.summary.totalCalls}</div>
                    <div className="text-sm text-muted-foreground mt-1">Total Calls</div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">{timeline.summary.totalDeliveries}</div>
                    <div className="text-sm text-muted-foreground mt-1">Content Delivered</div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">{timeline.summary.totalEngagements}</div>
                    <div className="text-sm text-muted-foreground mt-1">Engagements</div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">{timeline.summary.totalContacts}</div>
                    <div className="text-sm text-muted-foreground mt-1">Contacts</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Timeline Events */}
          <Card>
            <CardHeader>
              <CardTitle>Interaction Timeline</CardTitle>
              <p className="text-sm text-muted-foreground">
                Key events highlighted in yellow contributed to MQL qualification
              </p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                    <p className="text-muted-foreground">Loading engagement history...</p>
                  </div>
                </div>
              ) : !timeline || timeline.data.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No interaction history found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Timeline Events */}
                  <div className="relative space-y-4">
                    {/* Vertical Line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
                    
                    {timeline.data.map((event) => {
                      const EventIcon = getEventIcon(event);
                      const { date, time } = formatTimestamp(event.timestamp);
                      const isKeyEvent = isKeyQualificationEvent(event);
                      
                      return (
                        <div key={event.id} className="relative pl-16">
                          {/* Icon */}
                          <div className={`absolute left-0 w-12 h-12 rounded-full flex items-center justify-center ${getEventColor(event.color)} ${isKeyEvent ? 'ring-2 ring-yellow-400' : ''}`}>
                            <EventIcon className="h-5 w-5" />
                          </div>
                          
                          {/* Content */}
                          <div className={`rounded-lg p-4 hover:bg-muted/50 transition-colors ${isKeyEvent ? 'bg-yellow-50 border-2 border-yellow-200' : 'bg-muted/30'}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium">{event.title}</h4>
                                  {isKeyEvent && (
                                    <Badge variant="default" className="bg-yellow-500 text-yellow-950">
                                      Key Event
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">{event.description}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    {event.contactName}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">•</span>
                                  <span className="text-xs text-muted-foreground">{event.type.replace('_', ' ')}</span>
                                </div>
                              </div>
                              <div className="text-right text-sm text-muted-foreground ml-4">
                                <div>{date}</div>
                                <div className="text-xs">{time}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {timeline.pagination.totalPages > 1 && (
                    <div className="mt-6 pt-4 border-t">
                      <Pagination
                        currentPage={timeline.pagination.page}
                        totalPages={timeline.pagination.totalPages}
                        pageSize={timeline.pagination.limit}
                        totalItems={timeline.pagination.total}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={() => {}}
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
